import { FC, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import classes from './removeAllLinksModal.module.scss'

interface RemoveAllLinksModalProps {
  isOpen: boolean
  onCancel: () => void
  onConfirm: () => void
}

export const RemoveAllLinksModal: FC<RemoveAllLinksModalProps> = ({
  isOpen,
  onCancel,
  onConfirm,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onCancel()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onCancel])

  useDisableScroll(isOpen)

  if (!isOpen) return null

  return createPortal(
    <div className={classes.modalOverlay}>
      <div className={classes.modalContent} ref={ref}>
        <div className={classes.modalHeader}>
          <h2 className={classes.modalTitle}>Remove all links?</h2>
          <button
            className={classes.closeButton}
            onClick={onCancel}
            type="button"
          >
            <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>

        <div className={classes.modalBody}>
          <p className={classes.message}>
            This will permanently remove all added URLs from the list. You
            won&apos;t be able to recover them.
          </p>
        </div>

        <div className={classes.modalFooter}>
          <button
            className={classes.cancelButton}
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            className={classes.deleteButton}
            onClick={onConfirm}
            type="button"
          >
            Delete All
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
